import {
    ExtendedApartmentBuilding,
    ExtendedAreaModels,
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
} from '@bpd-library/utilities';
import { HouseSelectorModel } from '@features/properties/components/house-selector/utilities/types';
import createStore from '@utilities/store';

interface ViewConstructor<Type, DataItem> {
    expandedMap?: boolean;
    type: Type;
    data: ViewData;
    dataType?: 'default' | 'recommended' | 'soldConstructionNumbers' | 'soldProjects';
    slug?: string;
    name?: string | null;
    viewId?: string | null;
    onCardClick?: (event: MouseEvent, item: DataItem, cardType: ViewCardTypes) => void;
    onReturn?: () => void;
}

type ViewGrid = ViewConstructor<
    'propertyType' | 'apartmentPropertyType',
    ExtendedPropertyType | ExtendedApartmentBuilding | ExtendedProjectPhase
>;
type ViewList = ViewConstructor<
    'apartmentConstructionNumber' | 'constructionNumber',
    ExtendedConstructionNumber
>;

export type View = ViewList | ViewGrid;
export type ViewType =
    | 'propertyType'
    | 'apartmentPropertyType'
    | 'apartmentConstructionNumber'
    | 'constructionNumber';

export type ViewCardObject =
    | ExtendedConstructionNumber
    | ExtendedPropertyType
    | ExtendedApartmentBuilding
    | ExtendedProjectPhase;

export type ViewCardTypes = ViewCardObject['objectType'];

export type ViewData = ViewCardObject[];

export interface PropertiesState {
    view?: View;
    viewData?: ViewData;
    filteredData?: ExtendedAreaModels;
    soldProjectsData?: ExtendedAreaModels;
    allProjectsSold?: boolean;
    houseSelectorData?: HouseSelectorModel;
}

const UPDATE_VIEW = 'updateView';
const UPDATE_FILTER_DATA = 'updateFilterData';
const UPDATE_VIEW_DATA = 'updateViewData';
const UPDATE_SOLD_PROJECTS_DATA = 'updateSoldProjectsData';
const UPDATE_HOUSE_SELECTOR_DATA = 'updateHouseSelectorData';
const SET_ALL_PROJECTS_SOLD = 'setAllProjectsSold';

const initialState: PropertiesState = {};

const actions = {
    [UPDATE_FILTER_DATA]: (state: PropertiesState, filteredData: ExtendedAreaModels) => {
        state.filteredData = filteredData;
        return state;
    },
    [UPDATE_SOLD_PROJECTS_DATA]: (state: PropertiesState, soldProjectsData: ExtendedAreaModels) => {
        state.soldProjectsData = soldProjectsData;
        return state;
    },
    [UPDATE_VIEW]: (state: PropertiesState, view: View) => {
        state.view = view;
        return state;
    },
    [UPDATE_VIEW_DATA]: (state: PropertiesState, viewData: ViewData) => {
        state.viewData = viewData;
        return state;
    },
    [UPDATE_HOUSE_SELECTOR_DATA]: (state: PropertiesState, selectorData: HouseSelectorModel) => {
        state.houseSelectorData = selectorData;
        return state;
    },
    [SET_ALL_PROJECTS_SOLD]: (state: PropertiesState, allProjectsSold: boolean) => {
        state.allProjectsSold = allProjectsSold;
        return state;
    },
};

export const propertiesStore = createStore<PropertiesState>({
    initialState,
    actions,
});

export const updateFilteredData = (data: ExtendedAreaModels) => {
    propertiesStore.dispatch(UPDATE_FILTER_DATA, data);
};

export const updateSoldProjectsData = (data: ExtendedAreaModels) => {
    propertiesStore.dispatch(UPDATE_SOLD_PROJECTS_DATA, data);
};

export const updateView = (view: View) => {
    propertiesStore.dispatch(UPDATE_VIEW, view);
};

export const updateViewData = (viewData: ViewData) => {
    propertiesStore.dispatch(UPDATE_VIEW_DATA, viewData);
};

export const updateHouseSelectorData = (houseSelectorData: HouseSelectorModel) => {
    propertiesStore.dispatch(UPDATE_HOUSE_SELECTOR_DATA, houseSelectorData);
};

export const setAllProjectsSold = (allProjectsSold: boolean) => {
    propertiesStore.dispatch(SET_ALL_PROJECTS_SOLD, allProjectsSold);
};

export const getCurrentView = () => propertiesStore.getState().view;

export const getFilteredData = () => propertiesStore.getState().filteredData;

export const getSoldProjectsData = () => propertiesStore.getState().soldProjectsData;

export const getHouseSelectorData = () => propertiesStore.getState().houseSelectorData;

export const getHouseSelectorView = (viewId: string) => {
    const data = propertiesStore.getState().houseSelectorData;
    return data?.views?.find(({ id }) => id === viewId);
};

export const getHouseSelectorViewByObjectId = (id: string) => {
    const data = propertiesStore.getState().houseSelectorData;
    return data?.views?.find(({ polygons }) => {
        return polygons?.find(({ objectId }) => objectId === id);
    });
};
